import detectBrowserLanguage from 'detect-browser-language';
import { LANGUAGE_CODES } from './Languages';
import BackendApiClient from '../api/BackendApiClient';

export const LANGUAGE_KEY = 'LANGUAGE';
export const LANGUAGE_PARAM = 'lng';

class LocalizationManager {
  /**
   * Whether the language i18next started with came from the customer — the storefront
   * hand-off cookie, an explicit ?lng=, or a choice cached from an earlier visit —
   * rather than from browser detection or the plain default.
   *
   * This has to be recorded by the detector, at detect time. i18next caches whatever
   * it resolves straight back into localStorage, so a moment later "there is a stored
   * language" is true even on a first visit, and the restaurant's configured language
   * could never win.
   */
  #hasExplicitChoice = false;

  markExplicitChoice() {
    this.#hasExplicitChoice = true;
  }

  hasExplicitChoice() {
    return this.#hasExplicitChoice;
  }

  getLanguage() {
    return localStorage.getItem(LANGUAGE_KEY);
  }

  /**
   * Caches the language for this visit and tells the API which locale to answer in.
   *
   * Deliberately does NOT write the signed-in customer's account: i18next calls this
   * for every language it resolves, including the restaurant's default applied on
   * load. Persisting here would record a language the customer never chose — their
   * order emails would switch to the restaurant's language behind their back. The
   * account is written only where the customer actually picks one (see useLanguage).
   */
  saveLanguage(language) {
    localStorage.setItem(LANGUAGE_KEY, language);
    BackendApiClient.setFallbackLocale(language);
  }

  getLanguageFromUrl() {
    const url = window.location.href;
    const query = `${LANGUAGE_PARAM}?=`;

    if (url?.includes(query)) {
      return url?.split(query)[1] ?? null;
    }

    return null;
  }

  getPreferredLanguage() {
    const preferredBrowserLanguage = detectBrowserLanguage();
    const language = preferredBrowserLanguage?.split(/-|_/)[0];
    const defaultLanguage = LANGUAGE_CODES.find((item) => item === language);

    if (!defaultLanguage) {
      return null;
    }

    return defaultLanguage;
  }
}

export default new LocalizationManager();
